<template>
  <article
    class="igc"
    :class="mode === 'grid' ? 'igc--grid' : 'igc--list'"
    :style="{ '--igc-color': theme.color, '--igc-grad': theme.grad }"
    @click="$router.push(`/platform/intGrants/${item.id}`)"
  >
    <!-- ─── GRID MODE ─── -->
    <template v-if="mode === 'grid'">
      <div class="igc__g-header">
        <div class="igc__g-logo">
          <img :src="item.image || fallbackImage" :alt="item.title" />
        </div>
        <span v-if="featured" class="igc__hot">Топ</span>
      </div>
      <div class="igc__g-body">
        <h3 class="igc__g-name">{{ item.title }}</h3>
        <p class="igc__g-region">{{ regionLabel }}</p>
        <div class="igc__g-amount" v-if="item.grant_amount">
          {{ item.grant_amount }}
          <span class="igc__g-amount-hint"> / год</span>
        </div>
        <div class="igc__g-meta">
          <span v-if="item.country_name" class="igc__tag">{{ item.country_name }}</span>
          <span v-if="item.study_degree_name" class="igc__tag igc__tag--blue">{{ item.study_degree_name }}</span>
        </div>
      </div>
      <div class="igc__g-footer">
        <span class="igc__more" :style="{ color: theme.color }">
          Подробнее
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </span>
      </div>
    </template>

    <!-- ─── LIST MODE ─── -->
    <template v-else>
      <div class="igc__l-accent" />
      <div class="igc__l-logo">
        <img :src="item.image || fallbackImage" :alt="item.title" />
      </div>
      <div class="igc__l-body">
        <div class="igc__l-name-row">
          <h3 class="igc__l-name">{{ item.title }}</h3>
          <span v-if="featured" class="igc__hot">Топ</span>
        </div>
        <div class="igc__l-meta">
          <span v-if="item.country_name" class="igc__tag">
            <img v-if="item.country_flag" :src="item.country_flag" alt="" class="igc__flag" />
            {{ item.country_name }}
          </span>
          <span v-if="item.study_degree_name" class="igc__tag igc__tag--blue">{{ item.study_degree_name }}</span>
          <span v-if="item.subtitle && item.subtitle !== item.country_name" class="igc__tag igc__tag--muted">{{ item.subtitle }}</span>
        </div>
      </div>
      <div class="igc__l-right">
        <div v-if="item.grant_amount" class="igc__l-amount-block">
          <span class="igc__l-amount">{{ item.grant_amount }}</span>
          <span class="igc__l-hint">в год</span>
        </div>
        <span class="igc__more" :style="{ color: theme.color }">
          Подробнее
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </span>
      </div>
    </template>
  </article>
</template>

<script>
import { getGrantTheme, isFeaturedGrant } from '@/utils/grantTheme';

export default {
  props: {
    item: { type: Object, required: true },
    mode: { type: String, default: 'grid' }, // 'grid' | 'list'
  },
  data() {
    return { fallbackImage: require('@/assets/icons/course.webp') };
  },
  computed: {
    theme() { return getGrantTheme(this.item.id); },
    featured() { return isFeaturedGrant(this.item); },
    regionLabel() { return this.item.subtitle || this.item.country_name || ''; },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/grants';

// ── shared tokens ──────────────────────────────────
.igc {
  cursor: pointer;
  font-family: 'Manrope', sans-serif;
  -webkit-font-smoothing: antialiased;
  background: $grant-surface;
  border: 1px solid $grant-border;
  transition: box-shadow 0.18s, border-color 0.18s, transform 0.15s;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.05);

  &:hover {
    box-shadow: 0 6px 24px rgba(16, 24, 40, 0.1);
    border-color: rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
    .igc__more { gap: 8px; }
  }
}

// ── shared sub-elements ────────────────────────────
.igc__hot {
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 999px;
  padding: 3px 9px;
  white-space: nowrap;
  flex-shrink: 0;
}

.igc__tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  background: #f3f4f6;
  border-radius: 6px;
  padding: 4px 10px;
  line-height: 1.2;
  white-space: nowrap;

  &--blue  { color: #1d4ed8; background: #eff6ff; }
  &--muted { color: #9ca3af; background: transparent; padding-left: 0; }
}

.igc__flag {
  width: 16px; height: 11px;
  object-fit: cover; border-radius: 2px;
}

.igc__more {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  font-weight: 700;
  transition: gap 0.15s;
}

// ════════════════════════════════════════════════════
// GRID CARD
// ════════════════════════════════════════════════════
.igc--grid {
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .igc__g-header {
    background: var(--igc-grad);
    padding: 14px 14px 12px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    position: relative;
    min-height: 76px;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(160deg, rgba(255,255,255,0.06) 0%, transparent 60%);
      pointer-events: none;
    }
  }

  .igc__g-logo {
    width: 44px; height: 44px;
    border-radius: 10px;
    background: #fff;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
    padding: 6px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;

    img { width: 100%; height: 100%; object-fit: contain; }
  }

  .igc__g-body {
    padding: 12px 14px 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
  }

  .igc__g-name {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.01em;
    line-height: 1.3;
  }

  .igc__g-region {
    margin: 0;
    font-size: 11.5px;
    font-weight: 500;
    color: #6b7280;
    line-height: 1.35;
  }

  .igc__g-amount {
    font-size: 15px;
    font-weight: 500;
    color: #374151;
    letter-spacing: -0.01em;
    line-height: 1.3;
  }

  .igc__g-amount-hint {
    font-size: 11px;
    font-weight: 500;
    color: #9ca3af;
  }

  .igc__g-meta {
    display: flex; flex-wrap: wrap; gap: 6px;
  }

  .igc__g-footer {
    padding: 8px 14px 12px;
    border-top: 1px solid $grant-border;
    margin-top: auto;

    .igc__more {
      font-size: 12px;
      font-weight: 600;
    }
  }
}

// ════════════════════════════════════════════════════
// LIST CARD
// ════════════════════════════════════════════════════
.igc--list {
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 18px 22px;
  position: relative;

  &:hover .igc__l-accent { opacity: 1; }

  .igc__l-accent {
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 4px;
    border-radius: 14px 0 0 14px;
    background: var(--igc-color);
    opacity: 0.5;
    transition: opacity 0.18s;
  }

  .igc__l-logo {
    flex-shrink: 0;
    width: 52px; height: 52px;
    border-radius: 11px;
    border: 1px solid #f0f0f0;
    background: #fafafa;
    padding: 7px;
    display: flex; align-items: center; justify-content: center;

    img { width: 100%; height: 100%; object-fit: contain; }
  }

  .igc__l-body {
    flex: 1; min-width: 0;
    display: flex; flex-direction: column; gap: 8px;
  }

  .igc__l-name-row {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  }

  .igc__l-name {
    margin: 0;
    font-size: 15.5px;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.01em;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 260px;
  }

  .igc__l-meta {
    display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
  }

  .igc__l-right {
    flex-shrink: 0;
    text-align: right;
    display: flex; flex-direction: column;
    align-items: flex-end; gap: 10px;
  }

  .igc__l-amount-block {
    display: flex; flex-direction: column;
    align-items: flex-end; gap: 1px;
  }

  .igc__l-amount {
    font-size: 15px;
    font-weight: 500;
    color: #374151;
    letter-spacing: -0.01em;
    line-height: 1.3;
  }

  .igc__l-hint {
    font-size: 11px;
    font-weight: 500;
    color: #9ca3af;
  }

  @media (max-width: 600px) {
    padding: 14px 16px;
    gap: 14px;

    .igc__l-name  { font-size: 14px; max-width: 100%; }
    .igc__l-amount { font-size: 16px; }
  }
}
</style>
